import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Calendar, Loader2, RefreshCw, WifiOff } from 'lucide-react';
import ResourceCheckPage from '@/pages/ResourceCheckPage';
import UnsupportedDevicePage from '@/pages/UnsupportedDevicePage';
import { detectDeviceClass } from '@/lib/deviceSupport';
import InstructionsPage from '@/pages/InstructionsPage';
import InterviewPage from '@/pages/InterviewPage';
import InterviewCompletedPage from '@/pages/InterviewCompletedPage';
import { getSessionByLink, requestReschedule } from '@/services/apiClient';
import { storeStream, consumeStream } from '@/lib/streamStore';
import { Button } from '@/components/ui/button';

/* ── Disconnected / stuck in_progress screen ─────────────────────────────── */
function DisconnectedScreen({ session, uniqueLink, onResume }) {
  const navigate = useNavigate();
  const [reqState, setReqState] = useState('idle'); // idle | loading | done | error
  const [note, setNote] = useState('');

  const answered = session?.currentQuestionIndex ?? 0;
  const total    = session?.questions?.length ?? 0;
  const isDisconnected = session?.status === 'disconnected';
  const alreadyRequested = session?.rescheduleRequested;

  const handleRequestReschedule = async () => {
    setReqState('loading');
    try {
      await requestReschedule(session._id, { note });
      setReqState('done');
    } catch {
      setReqState('error');
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#0a0d14] px-4 py-10">
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-32 -top-32 h-[500px] w-[500px] rounded-full bg-amber-500/5 blur-[120px]" />
        <div className="absolute -bottom-32 -right-32 h-[400px] w-[400px] rounded-full bg-orange-500/5 blur-[100px]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>

      <div className="relative z-10 w-full max-w-lg">
        {/* Back */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-white/80 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Dashboard
          </button>
        </div>

        <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl p-8 space-y-6 text-center">
          {/* Icon */}
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-amber-500/30 bg-amber-500/10">
            <WifiOff className="h-8 w-8 text-amber-400" />
          </div>

          {/* Heading */}
          <div>
            <h1 className="text-2xl font-black text-white tracking-tight">
              {isDisconnected ? 'Interview Disconnected' : 'Interview In Progress'}
            </h1>
            <p className="mt-2 text-sm text-white/50">
              {isDisconnected
                ? 'Your session was interrupted due to a network or browser issue.'
                : 'It looks like you were mid-interview. You can resume where you left off.'}
            </p>
          </div>

          {/* Progress */}
          {total > 0 && (
            <div className="rounded-xl border border-white/10 bg-white/5 px-5 py-4 text-left">
              <p className="text-xs text-white/40 uppercase tracking-widest mb-1">Progress saved</p>
              <p className="text-lg font-bold text-white">{answered} of {total} questions answered</p>
              <div className="mt-2 h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full bg-amber-500 transition-all"
                  style={{ width: `${total > 0 ? Math.round((answered / total) * 100) : 0}%` }}
                />
              </div>
            </div>
          )}

          {/* Actions */}
          {reqState === 'done' || alreadyRequested ? (
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-4 text-sm text-emerald-400">
              ✓ Reschedule request sent. Your recruiter will be in touch shortly.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {/* Resume */}
              <Button
                size="lg"
                className="w-full gap-2 font-bold"
                onClick={onResume}
              >
                <RefreshCw className="h-4 w-4" />
                Resume Interview
              </Button>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[11px] text-white/30">or</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              {/* Optional note */}
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Optional: briefly describe what happened…"
                rows={2}
                className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs text-white placeholder:text-white/30 resize-none focus:outline-none focus:ring-1 focus:ring-amber-500/40"
              />

              {/* Request reschedule */}
              <Button
                size="lg"
                variant="outline"
                className="w-full gap-2 font-bold border-white/20 text-white hover:bg-white/10"
                disabled={reqState === 'loading'}
                onClick={handleRequestReschedule}
              >
                {reqState === 'loading' ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Sending request…</>
                ) : (
                  <><Calendar className="h-4 w-4" /> Request Reschedule</>
                )}
              </Button>

              {reqState === 'error' && (
                <p className="text-xs text-red-400">Failed to send request. Please try again or contact support.</p>
              )}
            </div>
          )}

          <p className="text-[11px] text-white/30 leading-relaxed">
            Resuming will continue from where you left off. Requesting a reschedule notifies your recruiter immediately.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * InstructionsRoute — handles the full pre-interview funnel:
 *   0. DisconnectedScreen — shown if session is disconnected or stuck in_progress
 *   1. ResourceCheckPage  — camera + mic + face detection
 *   2. InstructionsPage   — rules, tips, job context
 *
 * On step 1 the browser grants the media stream. We keep it alive (do NOT stop
 * it) so InterviewPage can use it immediately — no 20-30 second re-request.
 *
 * When the candidate clicks "Begin Interview", we navigate to /interview/:link
 * and pass the live MediaStream via React Router location state (in-memory only,
 * not serialised to the URL).
 */
export function InstructionsRoute() {
  const { uniqueLink } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState('loading'); // loading | unsupported | disconnected | resource | instructions
  // Resolved once, before anything else: a phone or tablet cannot complete a
  // proctored interview (see lib/deviceSupport), and finding that out after
  // the candidate has granted camera access is too late to be useful.
  const [deviceClass] = useState(detectDeviceClass);
  const [sessionInfo, setSessionInfo] = useState(null);
  // Keep the live stream across the ResourceCheck → Instructions transition
  const mediaStreamRef = useRef(null);

  // Fetch session metadata — also determines initial step
  useEffect(() => {
    getSessionByLink(uniqueLink)
      .then(res => {
        const session = res.data;
        setSessionInfo(session);
        const status = session?.status;
        if (deviceClass !== 'desktop') {
          setStep('unsupported');
        } else if (status === 'disconnected' || status === 'in_progress') {
          setStep('disconnected');
        } else {
          setStep('resource');
        }
      })
      .catch(() => {
        // Non-fatal — fall through even if session fetch fails. The device
        // block still applies: we know the browser without needing the server.
        setStep(deviceClass === 'desktop' ? 'resource' : 'unsupported');
      });
  }, [uniqueLink, deviceClass]);

  if (step === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0a0d14]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (step === 'unsupported') {
    return <UnsupportedDevicePage session={sessionInfo} deviceClass={deviceClass} />;
  }

  if (step === 'disconnected') {
    return (
      <DisconnectedScreen
        session={sessionInfo}
        uniqueLink={uniqueLink}
        onResume={() => setStep('resource')}
      />
    );
  }

  if (step === 'resource') {
    return (
      <ResourceCheckPage
        onSuccess={(stream) => {
          mediaStreamRef.current = stream;
          setStep('instructions');
        }}
      />
    );
  }

  return (
    <InstructionsPage
      session={sessionInfo}
      onBegin={() => {
        // Store stream in module-level variable — avoids DataCloneError from
        // browser extensions that hook history.replaceState and try to
        // structuredClone the navigation state (MediaStream is not cloneable).
        storeStream(mediaStreamRef.current);
        navigate(`/interview/${uniqueLink}`, { replace: true });
      }}
    />
  );
}

/**
 * InterviewRoute — renders the live interview.
 * Reads the pre-acquired MediaStream from the module-level streamStore
 * (set by InstructionsRoute just before navigation).
 * Falls back to getUserMedia if someone navigates here directly.
 */
export function InterviewRoute() {
  const { uniqueLink } = useParams();
  const navigate = useNavigate();
  // consumeStream() retrieves and clears the stored stream in one call.
  // Using a ref so the value is stable across re-renders.
  const initialStreamRef = useRef(consumeStream());

  // The same block as InstructionsRoute, repeated because this route is
  // reachable directly — a candidate who bookmarks /interview/:link, or
  // reopens it from history, never passes through the instructions flow.
  // The server refuses to start the session too; this is what stops the
  // interview UI from mounting and grabbing the camera first.
  if (detectDeviceClass() !== 'desktop') {
    return <UnsupportedDevicePage deviceClass={detectDeviceClass()} />;
  }

  return (
    <InterviewPage
      uniqueLink={uniqueLink}
      initialStream={initialStreamRef.current}
      onInterviewComplete={() => navigate('/interview-completed', { replace: true })}
    />
  );
}

export function InterviewCompletedRoute() {
  const navigate = useNavigate();
  return <InterviewCompletedPage onRedirect={() => navigate('/dashboard', { replace: true })} />;
}
